import { useState, useEffect, useRef } from 'react';
import AWS from 'aws-sdk';
import JsZip from 'jszip';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';

import Papa from 'papaparse';

// AWS.config.update will be set dynamically based on user input or session storage

function AudioConverterPolly() {
  const [csvData, setCsvData] = useState([]);
  const [serverResponses, setServerResponses] = useState([]);
  const [error, setError] = useState('');
  const [executionError, setExecutionError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0); // Progress state
  const [fileName, setFileName] = useState('');
  const [rangeValue, setRangeValue] = useState(90);
  const [engine, setEngine] = useState('long-form'); // Default engine

  // States for AWS configuration, initializing from sessionStorage or empty
  const [accessKeyId, setAccessKeyId] = useState(() => sessionStorage.getItem('awsAccessKeyId') || '');
  const [secretAccessKey, setSecretAccessKey] = useState(() => sessionStorage.getItem('awsSecretAccessKey') || '');
  const [awsRegion, setAwsRegion] = useState(() => sessionStorage.getItem('awsRegion') || 'us-east-1'); // Default to us-east-1

  // New states for checkbox control, defaulting to true (checked)
  const [saveAccessKeyId, setSaveAccessKeyId] = useState(true);
  const [saveSecretAccessKey, setSaveSecretAccessKey] = useState(true);

  const inputFileRef = useRef();
  const navigate = useNavigate();

  const { userLoggedIn } = useAuth();

  // List of all AWS regions
  const awsRegions = [
    'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
    'af-south-1', 'ap-east-1', 'ap-south-2', 'ap-southeast-1',
    'ap-southeast-2', 'ap-southeast-3', 'ap-southeast-4', 'ap-south-1',
    'ap-northeast-1', 'ap-northeast-2', 'ap-northeast-3',
    'ca-central-1', 'eu-central-1', 'eu-central-2', 'eu-west-1',
    'eu-west-2', 'eu-west-3', 'eu-north-1', 'eu-south-1',
    'eu-south-2', 'me-central-1', 'me-south-1', 'sa-east-1'
  ];

  useEffect(() => {
    if (!userLoggedIn) {
      navigate('/login');
    }
  }, [userLoggedIn, navigate]);

  // Update AWS config when accessKeyId, secretAccessKey, or awsRegion changes
  useEffect(() => {
    // Only update AWS.config if credentials are provided
    if (accessKeyId && secretAccessKey && awsRegion) {
      AWS.config.update({
        accessKeyId: accessKeyId,
        secretAccessKey: secretAccessKey,
        region: awsRegion
      });
    }
  }, [accessKeyId, secretAccessKey, awsRegion]);

  const handleAccessKeyChange = (e) => {
    const key = e.target.value;
    setAccessKeyId(key);
    if (saveAccessKeyId) { // Only save if checkbox is checked
      sessionStorage.setItem('awsAccessKeyId', key);
    }
  };

  const handleSecretAccessKeyChange = (e) => {
    const key = e.target.value;
    setSecretAccessKey(key);
    if (saveSecretAccessKey) { // Only save if checkbox is checked
      sessionStorage.setItem('awsSecretAccessKey', key);
    }
  };

  const handleRegionChange = (e) => {
    const region = e.target.value;
    setAwsRegion(region);
    sessionStorage.setItem('awsRegion', region); // Region is always saved for simplicity
  };

  const handleSaveAccessKeyIdToggle = (e) => {
    const isChecked = e.target.checked;
    setSaveAccessKeyId(isChecked);
    if (isChecked) {
      if (accessKeyId) { // Only save if there's a value in the input
        sessionStorage.setItem('awsAccessKeyId', accessKeyId);
      }
    } else {
      sessionStorage.removeItem('awsAccessKeyId');
    }
  };

  const handleSaveSecretAccessKeyToggle = (e) => {
    const isChecked = e.target.checked;
    setSaveSecretAccessKey(isChecked);
    if (isChecked) {
      if (secretAccessKey) { // Only save if there's a value in the input
        sessionStorage.setItem('awsSecretAccessKey', secretAccessKey);
      }
    } else {
      sessionStorage.removeItem('awsSecretAccessKey');
    }
  };


  const reset = () => {
    inputFileRef.current.value = "";
  };

  const resetForm = () => {
    inputFileRef.current.value = "";
    setProgress(0);
    setLoading(false);
    setFileName('');
    setCsvData([]);
    setServerResponses([]);
    setError('');
    setExecutionError(false);
    setRangeValue(90);
    setEngine('long-form'); // Reset engine to default
    // Do not reset access keys or region here as they are meant to persist based on checkbox state
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        setCsvData(results.data);
      },
    });
  };

  async function generateAndDownloadZip(records, setLoading, setProgress, engineType) {
    // Check if AWS credentials are set before proceeding
    if (!accessKeyId || !secretAccessKey || !awsRegion) {
      setError('Please provide AWS Access Key, Secret Access Key, and select a Region.');
      return;
    }

    setLoading(true);
    setProgress(0);
    const zip = new JsZip();
    const blobs = [];
    const serverResponses = [];
    const totalRecords = records.length;
    let processedRecords = 0;

    for (const record of records) {
      if (record.Text && record.VoiceId && record.Filename) {
        const params = {
          Engine: engineType,
          OutputFormat: 'mp3',
          Text: `<speak><prosody rate='${rangeValue}%'>${record.Text}</prosody></speak>`,
          VoiceId: record.VoiceId,
          TextType: "ssml",
        };

        try {
          const data = await new Promise((resolve, reject) => {
            const polly = new AWS.Polly(); // Polly instance uses the globally configured AWS credentials
            polly.synthesizeSpeech(params, (err, data) => {
              if (err) {
                setError(err.code);
                reset();
                reject(err);
              } else {
                resolve(data);
              }
            });
          });

          const uInt8Array = new Uint8Array(data.AudioStream);
          const arrayBuffer = uInt8Array.buffer;
          const blob = new Blob([arrayBuffer]);
          blobs.push({ blob, filename: record.Filename + '.mp3' });
          serverResponses.push(record.Filename + '.mp3');
        } catch (err) {
          setExecutionError(true);
          setError(err.code);
          console.log('Error synthesizing speech:', err);
        }
      }
      processedRecords++;
      setProgress((processedRecords / totalRecords) * 100);
    }

    if (!executionError) {
      blobs.forEach(({ blob, filename }) => {
        zip.file(filename, blob);
      });

      zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
        saveAs(zipBlob, `${fileName ? fileName : 'voiceovers'}.zip`);
        setLoading(false);
        setProgress(100);
      }).catch((err) => {
        setLoading(false);
        setProgress(0);
        console.error("Error generating zip", err);
      });

      setServerResponses(serverResponses);
    }
    reset();
  }

  return (
    <div>
      <section className="bg-gradient-to-br from-gray-900 to-indigo-900 to-black min-h-screen pt-20">
        <div className="block p-6 bg-white border border-gray-200 rounded-lg shadow-sm w-[600px] h-[auto] mx-auto text-black pt-14">

          {/* AWS Configuration Fields in one row */}
          <div className="flex flex-col md:flex-row md:space-x-4 mb-4">
            <div className="flex-1">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-left" htmlFor="aws_access_key_id">AWS Access Key ID</label>
              <input
                type="text"
                id="aws_access_key_id"
                className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                placeholder="Enter your AWS Access Key ID"
                value={accessKeyId}
                onChange={handleAccessKeyChange}
                 autocomplete="access-key-id"
              />
              <div className="flex items-center mt-1">
                <input
                  id="save_access_key_id_checkbox"
                  type="checkbox"
                  checked={saveAccessKeyId}
                  onChange={handleSaveAccessKeyIdToggle}
                 
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="save_access_key_id_checkbox" className="ml-2 text-xs font-medium text-[#0075ff] dark:text-gray-300">Save for this session</label>
              </div>
            </div>

            <div className="flex-1 mt-4 md:mt-0">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-left" htmlFor="aws_secret_access_key">AWS Secret Access Key</label>
              <input
                type="password"
                id="aws_secret_access_key"
                className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
                placeholder="Enter your AWS Secret Access Key"
                value={secretAccessKey}
                 autocomplete="new-password"
                onChange={handleSecretAccessKeyChange}
              />
              <div className="flex items-center mt-1">
                <input
                  id="save_secret_access_key_checkbox"
                  type="checkbox"
                  checked={saveSecretAccessKey}
                  onChange={handleSaveSecretAccessKeyToggle}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="save_secret_access_key_checkbox" className="ml-2 text-xs font-medium text-[#0075ff] dark:text-gray-300">Save for this session</label>
              </div>
            </div>

            <div className="flex-1 mt-4 md:mt-0">
              <label htmlFor="aws-region-select" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-left">AWS Region</label>
              <select
                id="aws-region-select"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                value={awsRegion}
                onChange={handleRegionChange}
              >
                {awsRegions.map((region) => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
              
            </div>
          </div>

          {/* Existing File Upload */}
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-left mt-4" htmlFor="file_input">Upload CSV file</label>
          <input
            className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400"
            aria-describedby="file_input_help"
            id="file_input"
            type="file"
            accept='.csv'
            onChange={handleFileUpload}
            ref={inputFileRef}
          />
          {!csvData.length && <label className="block mb-2 text-xs font-medium text-[#0075ff] text-left" htmlFor="file_input">Select the CSV file, ensure no empty rows!</label>}

          {/* Output zip filename */}
          <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white text-left mt-2">Output zip filename</label>
          <input
            type="text"
            className="block w-full text-sm p-2 text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 dark:text-gray-400 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 mt-1"
            placeholder="Enter the name of the zip file"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
          />

          {/* Engine Type Select */}
          <label htmlFor="engine-select" className="block mb-2 mt-2 align-left text-left text-sm font-medium text-gray-900 dark:text-white">Select Engine Type</label>
          <select
            id="engine-select"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
          >
            <option value="neural">Neural</option>
            <option value="long-form">Long-form</option>
            <option value="generative">Generative</option>
            <option value="standard">Standard</option>
          </select>

          {/* Voice Speed Range */}
          <label htmlFor="default-range" className="block mb-2 mt-2 align-left text-left text-sm font-medium text-gray-900 dark:text-white">Voice speed(%)</label>
          <div className="flex items-start mb-4">
            <input
              id="default-range"
              type="range"
              min="0"
              max="100"
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              value={rangeValue}
              onChange={(e) => {
                setRangeValue(+e.target.value);
              }}
            />
            <span className="ml-3 text-gray-700 text-sm font-medium dark:text-gray-300">{rangeValue}</span>
          </div>

          {/* Generate Button */}
          <button
            disabled={loading || fileName === '' || !accessKeyId || !secretAccessKey || !awsRegion}
            type="button"
            className="text-white cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-400 bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mt-4 w-full dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
            onClick={() => {
              if (csvData.length) {
                generateAndDownloadZip(csvData, setLoading, setProgress, engine);
              } else {
                setError('Please upload a CSV file first!');
                reset();
              }
            }}
          >
            {loading ? 'Processing...' : 'Generate'}
          </button>

          {/* Progress Bar */}
          {(loading || progress === 100) && (
            <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700 mt-4 mb-4">
              <div className="bg-green-700 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full" style={{ width: `${progress}%` }}> {Math.round(progress)}%</div>
            </div>
          )}

          {/* Processed Files List */}
          {serverResponses.length > 0 && (
            <div className='text-left text-sm overflow-y-scroll h-[150px]'>
              <h3 className="text-black font-bold">Processed: {csvData.length} files</h3>
              <ul className='h-[30px] overflow-y-[scroll] text-sm text-gray-900 dark:text-white'>
                {serverResponses.map((item, index) => (
                  <li key={index}>{item} &#x2705;</li>
                ))}
              </ul>
            </div>
          )}

          {/* Reset Button */}
          {(progress === 100) && (
            <button
              type="button"
              className="text-white cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-400 bg-gray-600 hover:bg-gray-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mt-4 w-full dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              onClick={() => {
                resetForm();
              }}
            >
              Reset
            </button>)
          }

          {/* Error Message */}
          {error && <div className='text-red-500 text-left text-sm'>Error: {error}</div>}

        </div>
      </section>
    </div >
  );
}

export default AudioConverterPolly;